/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { CssVariablesMapType } from '../interfaces/css-maps.interfaces';
import { CSS_VARS_HOST_TOKEN } from '../tokens/css-vars-host.token';
import { DEFAULT_THEME_TOKEN } from '../tokens/default-theme.token';

const DEFAULT_THEMES = {} as Record<string, CssVariablesMapType>;
type ThemesList = Record<string, CssVariablesMapType>;

@Injectable()
export class TestThemeSwitcherService {
  private readonly themeSubject$: BehaviorSubject<string>;
  public readonly theme$: Observable<string>;

  constructor(
    @Inject(DEFAULT_THEME_TOKEN)
    defaultTheme: string,
    @Inject(CSS_VARS_HOST_TOKEN)
    private readonly host: HTMLElement,
  ) {
    this.themeSubject$ = new BehaviorSubject(defaultTheme);
    this.theme$ = this.themeSubject$.asObservable();
  }

  public init$(themes: ThemesList = DEFAULT_THEMES): Observable<unknown> {
    return this.theme$.pipe(
      map((theme) => this.getTheme(theme, themes)),
      tap((varsMap) => this.setTheme(varsMap)),
    );
  }

  public getTheme(theme: string, themes: ThemesList): CssVariablesMapType {
    const varsMap = themes[theme];

    if (!varsMap) {
      throw new Error(`not found "${theme} theme"`);
    }

    return varsMap;
  }

  public updateTheme(value: string): void {
    this.themeSubject$.next(value);
  }

  private setTheme(varsMap: CssVariablesMapType): void {
    const cssVariables = Object.keys(varsMap) as (keyof CssVariablesMapType)[];

    cssVariables.forEach((cssVar) => {
      this.host.style.setProperty(cssVar.toString(), varsMap[cssVar] ?? null);
    });
  }
}
